// ===================================================
// Sidebar.js — 좌측 사이드바 컴포넌트 (데스크톱 전용)
//
// 구조:
//   [홈] 버튼 (소형)
//   [+ 이야기하기] CTA 버튼 (메인 액션)
//   ─────────────────
//   ▶ 키워드
//   ★ 즐겨찾기
//   📔 다이어리 / 📝 노트 / 📋 플래너 / 📚 학습
//      - 기본 접힘, 펼치면 오늘/이번주/이전 그룹
//      - 날짜 + 미리보기 텍스트 표시
//   ─────────────────
//   📅 기록  💬 나와의 대화  🔍 검색  ⚙️ 설정
// ===================================================

import { useState } from 'react';
import './Sidebar.css';
import { UI_TEXT } from './content/uiText';
import { MODE_CONFIG } from './content/modeConfig';
import { formatDate } from './utils/formatDate';
import { MODES } from './constants';
// 한 번에 보여줄 기록 수
const PAGE_SIZE = 5;

// 세션의 날짜를 기준으로 오늘/이번주/이전 그룹 레이블 반환
function getDateGroup(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sessionDate = new Date(dateStr);
  sessionDate.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today - sessionDate) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return '오늘';
  if (diffDays <= 6) return '이번 주';
  return '이전';
}

// 날짜 그룹 순서 (오늘 → 이번 주 → 이전)
const GROUP_ORDER = ['오늘', '이번 주', '이전'];

function Sidebar({ sessions, sessionsError, onRetryLoad, selectedSession, onSelectSession, onSelectKeywords,
  onOpenSearch, onHome, onOpenSettings, onOpenChat, onOpenCalendar, mode, screen, keywordsMode, hasUnreadChat }) {

  const T = UI_TEXT.sidebar;

  // 키워드 섹션 펼침 여부
  const [keywordsOpen, setKeywordsOpen] = useState(false);
  // 즐겨찾기 섹션 펼침 여부 (기본: 열림)
  const [pinnedOpen, setPinnedOpen] = useState(true);
  // 모드별 섹션 펼침 여부 (기본: 전체 접힘)
  const [expandedModes, setExpandedModes] = useState({});
  // 모드별 현재 보여주는 기록 수 (5개씩 늘어남)
  const [visibleCounts, setVisibleCounts] = useState({});

  // 모든 세션을 최신순으로 정렬
  const allSessions = [];
  Object.entries(sessions).forEach(([date, arr]) => {
    arr.forEach((s, idx) => allSessions.push({ date, idx, ...s }));
  });
  allSessions.sort((a, b) => b.date.localeCompare(a.date));

  // 즐겨찾기한 세션 목록 (pinned=true)
  const pinnedSessions = allSessions.filter(s => s.pinned);

  const isSessionActive = (s) =>
    screen === 'session-view' &&
    selectedSession?.date === s.date &&
    selectedSession?.idx === s.idx;

  const isKeywordsActive = (m) =>
    screen === 'keywords' && keywordsMode === m;

  // 섹션 접기/펼치기 — 접을 때는 보이는 수 초기화
  const toggleMode = (m) => {
    const isCurrentlyExpanded = expandedModes[m];
    if (isCurrentlyExpanded) {
      setVisibleCounts(prev => ({ ...prev, [m]: PAGE_SIZE }));
    }
    setExpandedModes(prev => ({ ...prev, [m]: !prev[m] }));
  };

  // 더보기 — 현재 보이는 수에서 PAGE_SIZE만큼 더 늘림
  const showMore = (m, total) => {
    setVisibleCounts(prev => ({
      ...prev,
      [m]: Math.min((prev[m] || PAGE_SIZE) + PAGE_SIZE, total),
    }));
  };

  return (
    <aside className={`sidebar ${mode}`}>

      {/* ── 이야기하기 CTA 버튼 ──────────────────────── */}
      <button
        className={`sb-cta ${screen === 'landing' || screen === 'qa' ? 'active' : ''}`}
        onClick={onHome}
      >
        <span className="sb-cta-plus">+</span>
        <span>{T.newRecord}</span>
      </button>

      <div className="sb-divider" />

      {/* ── 키워드 섹션 (토글) ───────────────────────── */}
      <div
        className={`sb-row sb-collapsible ${screen === 'keywords' ? 'section-active' : ''}`}
        onClick={() => setKeywordsOpen(p => !p)}
      >
        <span className={`sb-chevron ${keywordsOpen ? 'open' : ''}`}>›</span>
        <span className="sb-icon">🏷️</span>
        <span>{T.keywordsSection}</span>
      </div>

      {keywordsOpen && (
        <div className="sb-children">
          <div
            className={`sb-row sb-child ${isKeywordsActive(null) ? 'active' : ''}`}
            onClick={() => onSelectKeywords(null)}
          >
            <span className="sb-dot" />
            {T.allKeywords}
          </div>
          {MODES.map(m => (
            <div
              key={m}
              className={`sb-row sb-child ${isKeywordsActive(m) ? 'active' : ''}`}
              onClick={() => onSelectKeywords(m)}
            >
              <span className="sb-dot" />
              {MODE_CONFIG[m].icon} {MODE_CONFIG[m].label}
            </div>
          ))}
        </div>
      )}

      <div className="sb-divider" />

      {/* ── 즐겨찾기 섹션 ────────────────────────────── */}
      <div
        className="sb-row sb-collapsible"
        onClick={() => setPinnedOpen(p => !p)}
      >
        <span className={`sb-chevron ${pinnedOpen ? 'open' : ''}`}>›</span>
        <span className="sb-icon">★</span>
        <span>{T.pinnedSection}</span>
        {pinnedSessions.length > 0 && (
          <span className="sb-mode-count">{pinnedSessions.length}</span>
        )}
      </div>

      {pinnedOpen && (
        <div className="sb-children">
          {pinnedSessions.length === 0 ? (
            <div className="sb-empty">{T.noPinned}</div>
          ) : (
            pinnedSessions.map(session => {
              const m = session.mode;
              const itemTitle = (m === 'note' || m === 'decision') && session.title
                ? session.title
                : formatDate(session.date);
              return (
                <div
                  key={`${session.date}-${session.idx}`}
                  className={`sb-row sb-item ${isSessionActive(session) ? 'active' : ''}`}
                  onClick={() => onSelectSession(session.date, session.idx)}
                >
                  <span className="sb-dot" />
                  <div className="sb-item-body">
                    <span className="sb-item-text">{itemTitle}</span>
                    {session.initialText && (
                      <span className="sb-item-preview">{session.initialText}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <div className="sb-divider" />

      {/* ── 기록 로딩 오류 표시 ─────────────────────────── */}
      {sessionsError && (
        <div className="sb-load-error">
          <span className="sb-load-error-msg">기록을 불러오지 못했어요</span>
          <button className="sb-load-error-retry" onClick={onRetryLoad}>다시 시도</button>
        </div>
      )}

      {/* ── 모드별 기록 섹션 ─────────────────────────── */}
      {MODES.map(m => {
        const modeSessions = allSessions.filter(s => s.mode === m);
        const config = MODE_CONFIG[m];
        const isExpanded = !!expandedModes[m];
        // 현재 보여줄 기록 수 (펼쳤을 때만 의미 있음)
        const visibleCount = visibleCounts[m] || PAGE_SIZE;
        const shown = isExpanded ? modeSessions.slice(0, visibleCount) : [];
        const hasMore = isExpanded && visibleCount < modeSessions.length;
        const remainingCount = modeSessions.length - visibleCount;

        // 날짜 그룹별로 묶기 (오늘 → 이번 주 → 이전)
        const grouped = {};
        shown.forEach(session => {
          const group = getDateGroup(session.date);
          if (!grouped[group]) grouped[group] = [];
          grouped[group].push(session);
        });

        return (
          <div key={m} className="sb-mode-section">
            {/* 모드 헤더 — 항상 클릭 가능 */}
            <div
              className="sb-row sb-mode-header collapsible"
              onClick={() => toggleMode(m)}
            >
              <span className={`sb-chevron ${isExpanded ? 'open' : ''}`}>›</span>
              <span className="sb-icon">{config.icon}</span>
              <span className="sb-mode-label">{config.label}</span>
              {modeSessions.length > 0 && (
                <span className="sb-mode-count">{modeSessions.length}</span>
              )}
            </div>

            {/* 기록 목록 — 펼쳤을 때만 표시, 날짜 그룹별 구분 */}
            {isExpanded && (
              modeSessions.length === 0 ? (
                <div className="sb-empty">{T.empty}</div>
              ) : (
                <>
                  {GROUP_ORDER.filter(g => grouped[g]).map(groupLabel => (
                    <div key={groupLabel}>
                      {/* 날짜 그룹 레이블 (오늘/이번 주/이전) */}
                      <div className="sb-group-label">{groupLabel}</div>
                      {grouped[groupLabel].map(session => {
                        const itemTitle = (m === 'note' || m === 'decision') && session.title
                          ? session.title
                          : formatDate(session.date);
                        return (
                          <div
                            key={`${session.date}-${session.idx}`}
                            className={`sb-row sb-item ${isSessionActive(session) ? 'active' : ''}`}
                            onClick={() => onSelectSession(session.date, session.idx)}
                          >
                            <span className="sb-dot" />
                            <div className="sb-item-body">
                              <div className="sb-item-title-row">
                                <span className="sb-item-text">{itemTitle}</span>
                                {/* 즐겨찾기 항목에 ★ 표시 */}
                                {session.pinned && <span className="sb-item-star">★</span>}
                              </div>
                              {session.initialText && (
                                <span className="sb-item-preview">{session.initialText}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {/* 더보기 버튼 */}
                  {hasMore && (
                    <div className="sb-row sb-more-toggle" onClick={() => showMore(m, modeSessions.length)}>
                      <span className="sb-dot" />
                      <span className="sb-more-text">{T.moreRecords(remainingCount)}</span>
                    </div>
                  )}
                </>
              )
            )}
          </div>
        );
      })}

      {/* ── 하단 보조 네비게이션 ──────────────────────── */}
      <div className="sb-bottom">
        <div
          className={`sb-row ${screen === 'calendar' ? 'active' : ''}`}
          onClick={onOpenCalendar}
        >
          <span className="sb-icon">📅</span>
          <span>{T.calendarBtn}</span>
        </div>
        <div
          className={`sb-row sb-chat ${screen === 'chat' ? 'active' : ''}`}
          onClick={onOpenChat}
        >
          <span className="sb-icon">💬</span>
          <span>{T.chatBtn}</span>
          {hasUnreadChat && <span className="sb-unread-dot" />}
        </div>
        <div
          className={`sb-row sb-search ${screen === 'search' ? 'active' : ''}`}
          onClick={onOpenSearch}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && onOpenSearch()}
        >
          <span className="sb-icon">🔍</span>
          <span className="sb-search-placeholder">{T.searchPlaceholder}</span>
        </div>
        <div
          className={`sb-row sb-settings-btn ${screen === 'settings' ? 'active' : ''}`}
          onClick={onOpenSettings}
        >
          <span className="sb-icon">⚙️</span>
          <span>{T.settings}</span>
        </div>
      </div>

    </aside>
  );
}

export default Sidebar;
